import {
  forwardRef, useId, useState, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type TextareaHTMLAttributes,
} from 'react'
import { cx } from './primitives'

export interface FieldShellProps {
  label?: ReactNode
  description?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
}

/** Etiket + açıklama + hata çerçevesi; ARIA bağlantıları burada kurulur. */
export function FieldShell({
  id, label, description, error, required, className, children,
}: FieldShellProps & { id: string; children: ReactNode }) {
  return (
    <div className={cx('ui-field', className)}>
      {label && (
        <label htmlFor={id} className="ui-field__label">
          {label}{required && <span className="ui-field__required" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {description && !error && <span id={`${id}-desc`} className="ui-field__desc">{description}</span>}
      {error && typeof error !== 'boolean' && <span id={`${id}-err`} className="ui-field__error" role="alert">{error}</span>}
    </div>
  )
}

function describedBy(id: string, description?: ReactNode, error?: ReactNode) {
  if (error && typeof error !== 'boolean') return `${id}-err`
  if (description) return `${id}-desc`
  return undefined
}

type InputBase = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>

export interface TextFieldProps extends InputBase, FieldShellProps {
  leftSection?: ReactNode
  rightSection?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Giriş kutusunun dış sınıfı (çerçeve). */
  controlClassName?: string
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, description, error, required, className, leftSection, rightSection, size = 'md', controlClassName, id, disabled, ...rest }, ref,
) {
  const auto = useId()
  const fid = id ?? auto
  return (
    <FieldShell id={fid} label={label} description={description} error={error} required={required} className={className}>
      <div className={cx('ui-control', size !== 'md' && `ui-control--${size}`, error && 'ui-control--error', disabled && 'ui-control--disabled', controlClassName)}>
        {leftSection && <span className="ui-control__section">{leftSection}</span>}
        <input ref={ref} id={fid} className="ui-control__input" required={required} disabled={disabled}
          aria-invalid={error ? true : undefined} aria-describedby={describedBy(fid, description, error)} {...rest} />
        {rightSection && <span className="ui-control__section">{rightSection}</span>}
      </div>
    </FieldShell>
  )
})

/** Parola alanı: "Göster/Gizle" düğmesi erişilebilir adıyla gelir. */
export const PasswordField = forwardRef<HTMLInputElement, Omit<TextFieldProps, 'type' | 'rightSection'>>(function PasswordField(props, ref) {
  const [shown, setShown] = useState(false)
  return (
    <TextField ref={ref} {...props} type={shown ? 'text' : 'password'}
      rightSection={
        <button type="button" className="ui-control__toggle" onClick={() => setShown((v) => !v)}
          aria-label={shown ? 'Parolayı gizle' : 'Parolayı göster'} aria-pressed={shown}>
          {shown ? 'Gizle' : 'Göster'}
        </button>
      } />
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldShellProps {
  autosize?: boolean
  minRows?: number
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, description, error, required, className, id, autosize, minRows = 3, rows, onChange, style, ...rest }, ref,
) {
  const auto = useId()
  const fid = id ?? auto
  const grow = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (autosize) {
      e.currentTarget.style.height = 'auto'
      e.currentTarget.style.height = `${e.currentTarget.scrollHeight + 2}px`
    }
    onChange?.(e)
  }
  return (
    <FieldShell id={fid} label={label} description={description} error={error} required={required} className={className}>
      <textarea ref={ref} id={fid} rows={rows ?? minRows} required={required} onChange={grow}
        className={cx('ui-textarea', error && 'ui-textarea--error')} style={autosize ? { resize: 'none', ...style } : style}
        aria-invalid={error ? true : undefined} aria-describedby={describedBy(fid, description, error)} {...rest} />
    </FieldShell>
  )
})

export interface NumberFieldProps extends Omit<TextFieldProps, 'type' | 'value' | 'onChange'> {
  value: number | '' | null | undefined
  onChange: (v: number | '') => void
  min?: number
  max?: number
}

export function NumberField({ value, onChange, onBlur, min, max, ...rest }: NumberFieldProps) {
  // Alt sınır yazarken değil odak çıkınca uygulanır: aksi halde min=100 iken "2" yazılır yazılmaz 100'e döner.
  return (
    <TextField {...rest} type="number" inputMode="numeric" min={min} max={max}
      value={value ?? ''} onChange={(e) => {
        const raw = e.currentTarget.value
        if (raw === '') return onChange('')
        let n = Number(raw)
        if (Number.isNaN(n)) return
        if (max !== undefined) n = Math.min(max, n)
        onChange(n)
      }} onBlur={(e) => {
        if (min !== undefined && typeof value === 'number' && value < min) onChange(min)
        onBlur?.(e)
      }} />
  )
}

export interface CheckboxProps extends Omit<InputBase, 'type'> {
  label?: ReactNode
  description?: ReactNode
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, disabled, ...rest }, ref,
) {
  return (
    <label className={cx('ui-check', disabled && 'ui-check--disabled', className)}>
      <input ref={ref} type="checkbox" disabled={disabled} {...rest} />
      {(label || description) && (
        <span>{label}{description && <span className="ui-check__desc">{description}</span>}</span>
      )}
    </label>
  )
})

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: ReactNode
  description?: ReactNode
  disabled?: boolean
  'aria-label'?: string
  id?: string
}

/** Anahtar: role="switch", mürekkep zemin + limon topuz. */
export function Switch({ checked, onChange, label, description, disabled, id, ...aria }: SwitchProps) {
  const auto = useId()
  const fid = id ?? auto
  return (
    <span className="ui-switch">
      <button id={fid} type="button" role="switch" aria-checked={checked} disabled={disabled}
        aria-label={aria['aria-label']} aria-labelledby={label && !aria['aria-label'] ? `${fid}-l` : undefined}
        className="ui-switch__track" onClick={() => onChange(!checked)}>
        <span className="ui-switch__knob" />
      </button>
      {(label || description) && (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {label && <label id={`${fid}-l`} htmlFor={fid} style={{ cursor: 'pointer', fontWeight: 500 }}>{label}</label>}
          {description && <span className="ui-field__desc">{description}</span>}
        </span>
      )}
    </span>
  )
}

export interface SegmentedOption<T extends string> { value: T; label: ReactNode; icon?: ReactNode; disabled?: boolean; ariaLabel?: string; title?: string }

export function Segmented<T extends string>({
  value, onChange, options, size = 'md', full, 'aria-label': ariaLabel,
}: { value: T; onChange: (v: T) => void; options: SegmentedOption<T>[]; size?: 'sm' | 'md'; full?: boolean; 'aria-label'?: string }) {
  // Tek seçimli grup: radiogroup + radio. Ok tuşları seçimi gezdirir, Tab grubu tek durakta geçer.
  const enabled = options.filter((o) => !o.disabled)
  const move = (e: KeyboardEvent<HTMLButtonElement>, dir: number) => {
    e.preventDefault()
    const i = enabled.findIndex((o) => o.value === value)
    const next = enabled[(i + dir + enabled.length) % enabled.length]
    if (!next) return
    onChange(next.value)
    const group = e.currentTarget.parentElement
    requestAnimationFrame(() => group?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus())
  }
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cx('ui-seg', size === 'sm' && 'ui-seg--sm', full && 'ui-seg--full')}>
      {options.map((o) => {
        const on = o.value === value
        return (
          <button key={o.value} type="button" role="radio" className="ui-seg-item" aria-checked={on} tabIndex={on ? 0 : -1}
            aria-label={o.ariaLabel} title={o.title ?? o.ariaLabel}
            disabled={o.disabled} onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') move(e, 1)
              else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') move(e, -1)
            }}>
            {o.icon}{o.label}
          </button>
        )
      })}
    </div>
  )
}

export function Chip({
  checked, onChange, children, icon, 'aria-label': ariaLabel,
}: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; icon?: ReactNode; 'aria-label'?: string }) {
  return (
    <button type="button" className="ui-chip" aria-pressed={checked} aria-label={ariaLabel} onClick={() => onChange(!checked)}>
      {icon}{children}
    </button>
  )
}

/** Büyük, açıklamalı tek seçim kartı (görünürlük seçimi vb.). Grup role="radiogroup" ile sarılmalı. */
export function RadioCard({
  checked, onSelect, title, description, icon,
}: { checked: boolean; onSelect: () => void; title: ReactNode; description?: ReactNode; icon?: ReactNode }) {
  return (
    <button type="button" role="radio" aria-checked={checked} className="ui-radio-card" onClick={onSelect}>
      <span className="ui-radio-card__head">
        <span className="ui-radio-card__dot" aria-hidden="true" />
        {icon && <span className="ui-radio-card__icon" aria-hidden="true">{icon}</span>}
        <span className="ui-radio-card__title">{title}</span>
      </span>
      {description && <span className="ui-radio-card__desc">{description}</span>}
    </button>
  )
}
